import ServiceCustomerSelect from '@/components/Form/Shared/ServiceCustomerSelect'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    createSubcontract,
    genericSubcontractCode,
} from '@/modules/Contract/modules/Subcontract/services'
import { Subcontract } from '@/modules/Contract/modules/Subcontract/types'
import { CustomerContract } from '@/modules/Contract/types'
import { ModalProps } from '@/types/shared'
import { formatDateWithFormatter, omit } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaCreateSubcontractModal } from './schema'
import dayjs from 'dayjs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface CreateSubcontractFormValues
    extends Omit<
        Subcontract,
        'subcontract_id' | 'employee_id' | 'contract_id'
    > {
    useServiceID: boolean
}

export interface CreateSubcontractModalProps extends ModalProps {
    contract: CustomerContract
    contractID: number
}

const CreateSubcontractModal = ({
    onClose,
    open,
    contract,
    contractID,
}: CreateSubcontractModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'subcontracts',
        mode: 'contains',
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_SUBCONTRACTS_CODE, contractID],
        queryFn: () => genericSubcontractCode({ contractID }),
    })

    const formControl = useForm<CreateSubcontractFormValues>({
        defaultValues: {
            subcontract_code: data,
            subcontract_name: 'PLHĐ...',
            subcontract_effective_date: dayjs().format('YYYY-MM-DD'),
            subcontract_expiry_date: dayjs()
                .add(1, 'month')
                .format('YYYY-MM-DD'),
            subcontract_status: 'pending',
            service_ids: [],
            subcontract_register_date: null,
        },
        resolver: yupResolver(schemaCreateSubcontractModal as any),
    })

    useEffect(() => {
        formControl.setValue('subcontract_code', data as string)
    }, [data])

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createSubcontract },
        {
            successMessage: t('add-new-subcontract-success'),
            reactQueryKeys: QUERY_KEYS.SUBCONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const handleCreate = (values: CreateSubcontractFormValues) => {
        const form = omit(values, ['useServiceID'])
        if (values.useServiceID) {
            form.service_id = values.service_id
        }
        mutateAsync({
            data: {
                ...form,
                subcontract_effective_date: values.subcontract_effective_date
                    ? formatDateWithFormatter(
                          values.subcontract_effective_date,
                          'YYYY-MM-DD'
                      )
                    : undefined,
                subcontract_expiry_date: values.subcontract_expiry_date
                    ? formatDateWithFormatter(
                          values.subcontract_expiry_date,
                          'YYYY-MM-DD'
                      )
                    : undefined,
                subcontract_register_date: values.subcontract_register_date
                    ? formatDateWithFormatter(
                          values.subcontract_register_date,
                          'YYYY-MM-DD'
                      )
                    : null,
            },
            contractID,
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Dialog title={t('create-subcontract')} onClose={onClose} open={open}>
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('create-subcontract'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            // Read-only: the annex code is allocated server-side under a lock on
                            // the parent contract, so anything typed here would be discarded.
                            name: 'subcontract_code',
                            type: 'textfield',
                            label: t('subcontract_code'),
                            placeholder: t('subcontract_code'),
                            disabled: true,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract_name',
                            type: 'textfield',
                            label: t('subcontract_name'),
                            placeholder: t('subcontract_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract_effective_date',
                            type: 'datepicker',
                            label: t('subcontract_effective_date'),
                            placeholder: t('subcontract_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract_expiry_date',
                            type: 'datepicker',
                            label: t('subcontract_expiry_date'),
                            placeholder: t('subcontract_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract_register_date',
                            type: 'datepicker',
                            label: t('subcontract_register_date'),
                            placeholder: t('subcontract_register_date'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        renderEditComponent: (control) => {
                            return (
                                <ServiceCustomerSelect
                                    multiple
                                    eligibleForSubcontract
                                    label={t('subcontracts.service_id')}
                                    name="service_ids"
                                    control={control}
                                    customerID={Number(contract.customer_id)}
                                />
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract_note',
                            type: 'textarea',
                            label: t('subcontract_note'),
                            placeholder: t('subcontract_note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateSubcontractModal
